import type { JSX } from 'react';
import { ago } from '@/lib/format';
import { sourceGlyph } from '@/lib/glyphs';
import { failureFrom } from '@/lib/result';
import { StatusDot } from '../health/status-dot';
import { useSourceHealth } from '../health/use-source-health';
import { button, card, glyphTile } from '../shell/look';
import { EmptyState, ErrorState } from '../shell/states';
import { navigate } from '../shell/use-route';
import { Skeleton } from './live-activity';

/** Each source at a glance (design L343–358); a row opens that source. */
export function SourceHealthCard(): JSX.Element {
  const health = useSourceHealth();
  return (
    <div className={`${card} px-6 py-[22px]`}>
      <div className="mb-3.5 flex items-center justify-between">
        <div className="text-[15px] font-semibold">Source health</div>
        <button type="button" className={button.link} onClick={() => navigate({ page: 'health' })}>
          Details →
        </button>
      </div>
      {health.isPending && <Skeleton />}
      {health.isError && (
        <ErrorState {...failureFrom(health.error)} onRetry={() => void health.refetch()} />
      )}
      {health.data?.length === 0 && <EmptyState title="No sources connected yet" className="p-6" />}
      <div className="flex flex-col">
        {health.data?.map((row) => (
          <button
            key={row.instanceId}
            type="button"
            onClick={() => navigate({ page: 'sources', action: row.instanceId })}
            className="border-line hover:bg-surf2 grid w-full grid-cols-[34px_minmax(0,1fr)_auto_auto] items-center gap-3 border-0 border-t bg-transparent py-2.5 text-left text-inherit"
          >
            <span className={glyphTile}>{sourceGlyph(row.kind)}</span>
            <span className="truncate text-[13.5px]">{row.label}</span>
            <span className="text-mute font-mono text-[11px]">
              {row.lastSyncAt === undefined ? 'never' : ago(row.lastSyncAt)}
            </span>
            <StatusDot status={row.status} />
          </button>
        ))}
      </div>
    </div>
  );
}
